import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { bybitWebSocket, BybitPriceData } from '../services/bybitWebSocket';

interface BybitPriceContextType {
  getPriceBySymbol: (symbol: string) => number;
  getBidPrice: (symbol: string) => number;
  getAskPrice: (symbol: string) => number;
  getPriceData: (symbol: string) => BybitPriceData | null;
  getChange24h: (symbol: string) => number;
  isConnected: boolean;
  lastUpdateTime: number;
}

const BybitPriceContext = createContext<BybitPriceContextType | undefined>(undefined);

interface BybitPriceProviderProps {
  children: React.ReactNode;
}

export const BybitPriceProvider: React.FC<BybitPriceProviderProps> = ({ children }) => {
  const [prices, setPrices] = useState<Map<string, BybitPriceData>>(() => bybitWebSocket.getAllPrices());
  const [isConnected, setIsConnected] = useState(() => bybitWebSocket.isConnected());
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());

  useEffect(() => {
    bybitWebSocket.connect();

    const unsubscribePrices = bybitWebSocket.subscribe((newPrices) => {
      setPrices(new Map(newPrices));
      setLastUpdateTime(Date.now());
    });

    const unsubscribeStatus = bybitWebSocket.onStatusChange((status) => {
      setIsConnected(status === 'connected');
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !bybitWebSocket.isConnected()) {
        bybitWebSocket.connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodic forced refresh to ensure UI updates
    const refreshInterval = setInterval(() => {
      const currentPrices = bybitWebSocket.getAllPrices();
      if (currentPrices.size > 0) {
        setPrices(new Map(currentPrices));
        setLastUpdateTime(Date.now());
      }
    }, 1000);

    return () => {
      unsubscribePrices();
      unsubscribeStatus();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(refreshInterval);
    };
  }, []);

  const getPriceBySymbol = useCallback((symbol: string): number => {
    const data = prices.get(symbol);
    return data?.price || 0;
  }, [prices]);

  const getBidPrice = useCallback((symbol: string): number => {
    const data = prices.get(symbol);
    return data?.bid || 0;
  }, [prices]);

  const getAskPrice = useCallback((symbol: string): number => {
    const data = prices.get(symbol);
    return data?.ask || 0;
  }, [prices]);

  const getPriceData = useCallback((symbol: string): BybitPriceData | null => {
    return prices.get(symbol) || null;
  }, [prices]);

  const getChange24h = useCallback((symbol: string): number => {
    const data = prices.get(symbol);
    return data?.change24h || 0;
  }, [prices]);

  const contextValue: BybitPriceContextType = {
    getPriceBySymbol,
    getBidPrice,
    getAskPrice,
    getPriceData,
    getChange24h,
    isConnected,
    lastUpdateTime
  };

  return (
    <BybitPriceContext.Provider value={contextValue}>
      {children}
    </BybitPriceContext.Provider>
  );
};

export const useBybitPrices = (): BybitPriceContextType => {
  const context = useContext(BybitPriceContext);
  if (context === undefined) {
    throw new Error('useBybitPrices must be used within a BybitPriceProvider');
  }
  return context;
};
